import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setToken } from './api';

/**
 * StaffDesk is a single workspace (unlike GovFin's ministry/finance/treasury
 * split) — everyone with StaffDesk access lands in the same shell and routes
 * by section, not by persona type. `mdaCode` is carried for convenience
 * (defaulting the org-chart/officer-search MDA filter, matching an officer to
 * their own MDA_ADMIN:<mdaCode> role) — see the build report for why it was
 * added beyond the plan's bare minimum. `sub` is the bearer token's own
 * subject claim, exposed so the UI can offer "assign to me" and grey out
 * role-grant approvals the current officer proposed themself; this is not a
 * new information disclosure; the JWT the client already holds encodes the
 * same subject claim in its (unencrypted, base64url) payload.
 */
export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  mdaCode: string;
  sub: string;
  roles: string[];
  trustTier?: string;
}

/** MDA codes this persona administers, derived from `MDA_ADMIN:<mda>` role claims. */
export function adminMdaCodes(roles: string[]): string[] {
  return roles.filter((r) => r.startsWith('MDA_ADMIN:')).map((r) => r.slice('MDA_ADMIN:'.length));
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('staffdesk.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('staffdesk.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('staffdesk.persona');
    setPersona(null);
  }, []);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
